import React, { useState, useEffect } from 'react';
import { getActivityLogs, getActivityStats, subscribeToActivityLogs, unsubscribeChannel } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ActivityFilters from './components/ActivityFilters';
import ActivityTable from './components/ActivityTable';
import ActivityStats from './components/ActivityStats';

const ActivityLog = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  useEffect(() => {
    loadActivityData();

    // Subscribe to real-time activity log changes
    const channel = subscribeToActivityLogs(() => {
      loadActivityData();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeChannel(channel);
    };
  }, [filters]);

  const loadActivityData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [logsData, statsData] = await Promise.all([
        getActivityLogs(filters),
        getActivityStats()
      ]);
      
      setActivities(logsData || []);
      setStats(statsData);
    } catch (err) {
      console.error('Activity log load error:', err);
      setError(err?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      module: '',
      action: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  };

  const handleExport = () => {
    try {
      const csvContent = [
        ['Timestamp', 'User', 'Role', 'Module', 'Action', 'Entity', 'Description']?.join(','),
        ...activities?.map(activity => [
          new Date(activity?.createdAt)?.toLocaleString(),
          activity?.userName,
          activity?.userRole,
          activity?.module,
          activity?.action,
          activity?.entityName,
          `"${activity?.description}"`
        ]?.join(','))
      ]?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      a?.click();
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export activity logs');
    }
  };

  if (loading && !activities?.length) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading activity logs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <button
            onClick={loadActivityData}
            className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track user actions, status changes, and data modifications
          </p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Icon name="Download" className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
      {/* Statistics */}
      {stats && <ActivityStats stats={stats} />}
      {/* Filters */}
      <ActivityFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />
      {/* Activity Table */}
      <ActivityTable activities={activities} loading={loading} />
      {/* Empty State */}
      {!loading && activities?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
          <p className="text-muted-foreground">
            {Object.values(filters)?.some(v => v) 
              ? 'Try adjusting your filters' :'Activity logs will appear here as users perform actions'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;